import React, { useEffect } from 'react';
import { useGithub } from '../hooks/useGithub';
import { Repository } from '../types';

interface RepositorySelectorProps {
  onSelect?: (repo: Repository) => void;
}

const RepositorySelector: React.FC<RepositorySelectorProps> = ({ 
  onSelect 
}) => {
  const { auth, loading, repositories, fetchRepositories, selectedRepo, selectRepository } = useGithub();
  
  // Fetch repositories on mount if authenticated
  useEffect(() => {
    if (auth && repositories.length === 0) {
      fetchRepositories();
    }
  }, [auth, repositories.length, fetchRepositories]);
  
  // Handle repository selection
  const handleRepoSelect = (repo: Repository) => {
    selectRepository(repo);
    if (onSelect) {
      onSelect(repo);
    }
  };
  
  if (!auth) {
    return (
      <div className="text-center p-4 bg-yellow-50 border border-yellow-200 rounded-md">
        <p className="text-yellow-700">
          Please login with GitHub to select a repository.
        </p>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="text-center p-4">
        <p className="text-gray-500">Loading repositories...</p>
      </div>
    );
  }
  
  if (repositories.length === 0) {
    return (
      <div className="text-center p-4 bg-yellow-50 border border-yellow-200 rounded-md">
        <p className="text-yellow-700">
          No repositories found for your account.
        </p>
        <button 
          onClick={() => fetchRepositories()}
          className="mt-2 px-4 py-2 bg-yellow-200 rounded hover:bg-yellow-300"
        >
          Refresh
        </button>
      </div>
    );
  }
  
  return (
    <div className="repository-selector">
      <h3 className="text-lg font-medium mb-2">Select Repository</h3>
      
      <div className="border border-gray-300 rounded-md overflow-hidden">
        <div className="max-h-60 overflow-y-auto">
          {repositories.map((repo) => (
            <div 
              key={repo.id}
              className={`p-3 border-b border-gray-300 cursor-pointer hover:bg-gray-50 flex items-center ${
                selectedRepo?.id === repo.id ? 'bg-blue-50' : ''
              }`}
              onClick={() => handleRepoSelect(repo)}
            >
              <div className="flex-1">
                <div className="font-medium">{repo.name}</div>
                <div className="text-sm text-gray-600">{repo.description || 'No description'}</div>
              </div>
              {selectedRepo?.id === repo.id && (
                <div className="text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      
      {selectedRepo && (
        <div className="mt-3 bg-gray-50 p-3 rounded-md border border-gray-200">
          <div className="text-sm font-medium">Selected: {selectedRepo.full_name}</div>
          <div className="text-xs text-gray-600 mt-1">Default branch: {selectedRepo.default_branch}</div>
        </div>
      )}
    </div>
  );
};

export default RepositorySelector; 